import { Anchor, Button, Group, Paper, Radio, Stack, Text, Textarea, TextInput } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconDeviceFloppy, IconExternalLink } from '@tabler/icons-react'
import { observer } from 'mobx-react-lite'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { AIInputWrapper } from '@/components/AIInputWrapper'
import { InputLabel } from '@/components/InputLabel'
import { LiveModuleBanner } from '@/components/LiveModuleBanner'
import { RadioCard } from '@/components/RadioCard'
import { theme } from '@/configs/theme'
import { useAIFieldGeneration } from '@/hooks/useAIFieldGeneration'
import { AddStepDataContentModal } from '@/screens/ContentModuleFormScreen/AddStepDataContentModal'
import type { CMFormSection } from '@/screens/ContentModuleFormScreen/ContentModuleFormScreen'
import { FAQSection } from '@/screens/ContentModuleFormScreen/FAQSection'
import { UploadedFilePreview } from '@/screens/ContentModuleFormScreen/UploadedFilePreview'
import { type ContentAccessType, useReviewModalState } from '@/screens/ContentModuleFormScreen/useReviewModalState'
import type { ResourceType } from '@/services/api/api.types'
import * as commonClasses from '@/styles/componentCommons.css'
import { castStepToZodForm, castZodFormToStep } from '@/utils/contentModuleForm'

export const ReviewStepModal = observer(function ReviewStepModal(props: CMFormSection) {
  const [descriptionError, setDescriptionError] = useState<string | null>(null)

  const {
    pendingFileName,
    setPendingFileName,
    contentAccessType,
    setContentAccessType,
    showInApp,
    useInRag,
    pendingDescription,
    setPendingDescription,

    descriptionAIState,
    descriptionSuggestion,
    generateSourceFileDescription,
    generateSourceFileDescriptionWithGuide,
    acceptDescriptionSuggestion,
    rejectDescriptionSuggestion,
    cancelDescriptionGeneration,

    autoGenerationTriggeredRef,
    descriptionAutoGenTriggeredRef,
    faqSectionRef,

    hasContent,
    hasExtractedContent,
    isMutating,
    uploadedFileResource,
    sourceFileResource,
  } = useReviewModalState(props)

  const {
    getFieldState,
    generateFieldByName,
    regenerateFieldByName,
    acceptStepSuggestion,
    rejectSuggestion,
    cancelGeneration,
  } = useAIFieldGeneration(props)

  const openDataContentModal = useCallback(() => {
    props.openModal?.({
      title: `Edit File Content Manually`,
      subtitle: 'Copy and paste or enter your content manually.',
      Component: AddStepDataContentModal,
      ComponentProps: {
        initialContent: props.stepContentQuery.data?.content ?? '',
      },
      ModalProps: {
        size: '95vw',
      },
    })
  }, [props.stepContentQuery.data, props.openModal])

  const updateStep = useCallback(async () => {
    const titleAIState = getFieldState('name', 'step')
    const titleValue = props.stepForm.getValues().name

    if (!titleValue?.trim() && titleAIState.state === 'pending') {
      props.stepForm.setFieldError('name', 'Please accept or dismiss the suggested title before saving.')
      notifications.show({
        title: 'Could Not Submit Form',
        message: 'Please accept or dismiss the suggested title before saving.',
        color: 'red',
        classNames: commonClasses.notificationFilled,
        position: 'bottom-left',
      })
      return
    }

    const validate = props.stepForm.validateField
    const validations = [validate('name')]

    if (validations.some(v => v.hasError)) {
      notifications.show({
        title: 'Could Not Submit Form',
        message: 'There was an issue submitting file details. Please check the form for errors.',
        color: 'red',
        classNames: commonClasses.notificationFilled,
        position: 'bottom-left',
      })

      return
    }

    if (!pendingDescription?.trim() && descriptionAIState === 'pending') {
      setDescriptionError('Please accept or dismiss the suggested file description before saving.')
      notifications.show({
        title: 'Could Not Submit Form',
        message: 'Please accept or dismiss the suggested file description before saving.',
        color: 'red',
        classNames: commonClasses.notificationFilled,
        position: 'bottom-left',
      })
      return
    }

    if (!pendingDescription?.trim()) {
      setDescriptionError('File description is required.')
      notifications.show({
        title: 'Could Not Submit Form',
        message: 'File description is required.',
        color: 'red',
        classNames: commonClasses.notificationFilled,
        position: 'bottom-left',
      })

      return
    }

    setDescriptionError(null)

    try {
      await faqSectionRef.current?.saveUnsavedFAQs()

      const formTitle = props.stepForm.getValues().name

      if (pendingFileName && uploadedFileResource) {
        await props.resourceUpdateMutation.mutateAsync({
          resource_id: uploadedFileResource.resourceId,
          step_id: props.stepId,
          file_name: pendingFileName,
          type: uploadedFileResource.type as ResourceType,
          title: formTitle || uploadedFileResource.title,
          description: uploadedFileResource.description,
          keywords: uploadedFileResource.keywords || [],
          is_approved: true,
        })
      }

      const source = sourceFileResource
      if (source) {
        const hasShowInAppChange = showInApp !== (source.showInApp ?? false)
        const hasDescriptionChange = pendingDescription !== null && pendingDescription !== (source.description ?? '')
        const hasTitleChange = formTitle && formTitle !== source.title

        if (hasShowInAppChange || hasDescriptionChange || hasTitleChange) {
          await props.resourceUpdateMutation.mutateAsync({
            resource_id: source.resourceId,
            step_id: props.stepId,
            file_name: source.fileName ?? undefined,
            type: source.type as ResourceType,
            title: formTitle || source.title,
            description: pendingDescription ?? source.description,
            keywords: source.keywords || [],
            show_in_app: showInApp,
            is_approved: true,
          })
        }
      }

      await new Promise((resolve, reject) => {
        props.stepUpdateMutation.mutate(
          {
            ...castZodFormToStep(props.stepForm.getValues()),
            step_id: props.stepId!,
            use_in_rag: useInRag,
          },
          {
            onSuccess: () => {
              props.signalStepSaved?.()
              props.closeModal(() => {
                props.stepForm.setValues(castStepToZodForm())
                props.faqActions.resetStepFAQState()
                setPendingFileName(null)
              })
              resolve(undefined)
            },
            onError: reject,
          },
        )
      })
    } catch (error) {
      console.error('Failed to save step:', error)

      if (error instanceof Error && error.message === 'FAQ_VALIDATION_ERROR') {
        return
      }

      notifications.show({
        title: 'Could Not Save Step',
        message: 'There was an issue saving your step and FAQs. Please try again.',
        color: 'red',
        classNames: commonClasses.notificationFilled,
        position: 'bottom-left',
      })
      return
    }
  }, [
    props.stepForm,
    props.resourceUpdateMutation,
    props.stepId,
    props.stepUpdateMutation,
    props.signalStepSaved,
    props.closeModal,
    props.faqActions,
    uploadedFileResource,
    sourceFileResource,
    pendingFileName,
    pendingDescription,
    descriptionAIState,
    getFieldState,
    showInApp,
    useInRag,
    faqSectionRef,
  ])

  const handleCancel = useCallback(() => {
    props.closeModal(() => {
      props.stepForm.setValues(castStepToZodForm())
      props.faqActions.resetStepFAQState()
    })
  }, [props.closeModal, props.stepForm, props.faqActions])

  // Reset form errors when stepId changes
  useEffect(() => {
    props.stepForm.clearErrors()
    autoGenerationTriggeredRef.current = null
  }, [props.stepId])

  // Auto-generate title when content is available and processing is complete.
  // Sets the ref guard when the name is already populated, so clearing an existing title
  // does not trigger auto-generation (only newly created steps with blank titles).
  useEffect(() => {
    const isStepCreated = props.cmQuery.isSuccess && props.stepsQuery.isSuccess
    const currentName = props.stepForm.getValues().name
    const shouldTrigger = isStepCreated
    const generationKey = `${props.stepId}-${!!props.stepContentQuery.data}`

    if (props.stepId && hasExtractedContent && shouldTrigger && autoGenerationTriggeredRef.current !== generationKey) {
      autoGenerationTriggeredRef.current = generationKey

      if (currentName) return

      const step = props.stepsQuery.data?.find(s => s.stepId === props.stepId)
      if (step?.titleAi?.trim()) return

      generateFieldByName('step', 'name')
    }
  }, [
    props.stepId,
    props.stepsQuery.data,
    props.cmQuery.isSuccess,
    props.stepsQuery.isSuccess,
    !!props.stepContentQuery.data,
    generateFieldByName,
    props.stepForm.getValues().name,
    hasExtractedContent,
  ])

  // Auto-generate description when description is genuinely empty.
  // Sets the ref guard when description is already populated, so clearing an existing
  // description does not trigger auto-generation.
  useEffect(() => {
    const isStepCreated = props.cmQuery.isSuccess && props.stepsQuery.isSuccess
    const generationKey = `${props.stepId}-${sourceFileResource?.resourceId}-description`
    const descriptionEmpty = !(pendingDescription?.trim() || sourceFileResource?.description?.trim())

    if (
      props.stepId &&
      hasExtractedContent &&
      sourceFileResource !== undefined &&
      isStepCreated &&
      descriptionAIState === 'idle' &&
      descriptionAutoGenTriggeredRef.current !== generationKey
    ) {
      descriptionAutoGenTriggeredRef.current = generationKey

      if (!descriptionEmpty) return

      generateSourceFileDescription()
    }
  }, [
    props.stepId,
    hasExtractedContent,
    pendingDescription,
    descriptionAIState,
    props.cmQuery.isSuccess,
    props.stepsQuery.isSuccess,
    sourceFileResource,
  ])

  const isYoutube = sourceFileResource?.type === 'youtube'
  const aiDisabled = !hasContent || (isYoutube && (!hasExtractedContent || contentAccessType === 'media_only'))
  const showTranscript = !isYoutube || contentAccessType !== 'media_only'
  const fieldsDisabled = isYoutube && contentAccessType !== 'media_only' ? !hasExtractedContent : !hasContent

  const showAccessTypePicker = sourceFileResource?.showInAppSupported && hasContent

  const CONTENT_PREVIEW_LIMIT = 2000
  const contentPreview = useMemo(() => {
    const raw = props.stepContentQuery.data?.content ?? ''
    if (raw.length <= CONTENT_PREVIEW_LIMIT) return raw
    return raw.slice(0, CONTENT_PREVIEW_LIMIT) + '…'
  }, [props.stepContentQuery.data?.content])

  return (
    <Stack bg={theme.colors.gray[0]} gap="xl" pb="xl">
      {props.isLive && <LiveModuleBanner />}
      {/* Uploaded File Preview */}
      {(uploadedFileResource ||
        (sourceFileResource && (sourceFileResource.type === 'url' || sourceFileResource.type === 'youtube'))) && (
        <Paper shadow="sm" p="md" radius="md">
          <Text size="lg" fw={600} mb={uploadedFileResource ? 'xs' : 'sm'}>
            {isYoutube ? 'Preview Media' : 'Preview Source Content'}
          </Text>
          {uploadedFileResource ? (
            <UploadedFilePreview
              fileName={pendingFileName || uploadedFileResource.fileName || 'Uploaded file'}
              type={uploadedFileResource.type}
              resourceKey={uploadedFileResource.resourceKey}
              resourceData={uploadedFileResource.data}
              onFileNameChange={setPendingFileName}
            />
          ) : (
            <Paper withBorder p="sm" radius="sm" bg="gray.1">
              {sourceFileResource!.data || sourceFileResource!.sourceUrl ? (
                <Anchor
                  href={sourceFileResource!.data || sourceFileResource!.sourceUrl || ''}
                  target="_blank"
                  size="sm">
                  <Group gap={6}>
                    {sourceFileResource!.data || sourceFileResource!.sourceUrl}
                    <IconExternalLink size={14} />
                  </Group>
                </Anchor>
              ) : (
                <Text c="dimmed" size="sm">
                  URL unavailable
                </Text>
              )}
            </Paper>
          )}
        </Paper>
      )}

      {/* Review Section */}
      <Paper shadow="sm" p="md" radius="md">
        <Stack gap="xxl">
          <Text size="lg" fw={600}>
            {isYoutube ? 'Edit & Configure Media' : 'Edit & Configure Content'}
          </Text>
          {/* Content Access Type */}
          {showAccessTypePicker && (
            <Radio.Group
              label="Content Access"
              description="Choose how this content will be used."
              value={contentAccessType}
              onChange={v => setContentAccessType(v as ContentAccessType)}>
              <Stack gap="sm" mt="xs">
                <RadioCard
                  label="Full Access"
                  description={
                    isYoutube
                      ? "Sidekick can use the transcript to answer questions and can show the original YouTube video when it's relevant."
                      : "Sidekick can use the extracted content to answer questions and can show the original file (such as a video or PDF) when it's relevant."
                  }
                  value="full_access"
                />
                <RadioCard
                  label="Information Only"
                  description={
                    isYoutube
                      ? "Sidekick can use the transcript to answer questions, but it won't show the original YouTube video."
                      : "Sidekick can use the extracted content to answer questions, but it won't show the original file."
                  }
                  value="information_only"
                />
                <RadioCard
                  label="Media Only"
                  description={
                    isYoutube
                      ? "Sidekick can show the YouTube video, but it won't use the transcript to answer questions."
                      : "Sidekick can show the original file, but it won't use the extracted content to answer questions."
                  }
                  value="media_only"
                />
              </Stack>
            </Radio.Group>
          )}

          {/* Transcript / Extracted Content (YouTube: before title; others: after description) */}
          {isYoutube && showTranscript && (
            <Stack gap="xs">
              <Group justify="space-between">
                <InputLabel label="Transcript" withAsterisk />
                {hasExtractedContent && (
                  <Button variant="light" size="md" radius="md" onClick={openDataContentModal}>
                    Edit
                  </Button>
                )}
              </Group>
              {hasExtractedContent ? (
                <Textarea
                  value={contentPreview}
                  readOnly
                  placeholder="Add the transcript for your media so the Sidekick can navigate the media"
                  radius="md"
                  variant="filled"
                  size="sm"
                  rows={6}
                  style={{ border: '1px solid var(--mantine-color-gray-3)', borderRadius: theme.radius.md }}
                />
              ) : (
                <Paper
                  withBorder
                  p="xl"
                  radius="md"
                  bg="gray.1"
                  style={{
                    minHeight: 150,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                  <Stack align="center" gap="sm">
                    <Text size="sm" c="dimmed" ta="center">
                      Add the transcript for your media so the Sidekick can navigate it
                    </Text>
                    <Button onClick={openDataContentModal} disabled={!hasContent}>
                      Add Transcript
                    </Button>
                  </Stack>
                </Paper>
              )}
            </Stack>
          )}

          {/* Title */}
          <AIInputWrapper
            fieldState={getFieldState('name', 'step')}
            onAskSidekick={() => generateFieldByName('step', 'name')}
            onAskSidekickWithGuide={instructions =>
              generateFieldByName('step', 'name', { user_instructions: instructions })
            }
            onAccept={() => acceptStepSuggestion('name')}
            onReject={() => rejectSuggestion('name', 'step')}
            onCancel={() => cancelGeneration('name', 'step')}
            disabled={aiDisabled}
            onRegenerate={() => regenerateFieldByName('step', 'name')}>
            <TextInput
              withAsterisk
              label="Title"
              radius="md"
              disabled={fieldsDisabled}
              {...props.stepForm.getInputProps('name')}
            />
          </AIInputWrapper>

          {/* Description */}
          <AIInputWrapper
            fieldState={{ state: descriptionAIState, suggestion: descriptionSuggestion }}
            onAskSidekick={generateSourceFileDescription}
            onAskSidekickWithGuide={instructions => generateSourceFileDescriptionWithGuide(instructions)}
            onAccept={() => {
              acceptDescriptionSuggestion()
              setDescriptionError(null)
            }}
            onReject={rejectDescriptionSuggestion}
            onCancel={cancelDescriptionGeneration}
            disabled={aiDisabled}
            onRegenerate={generateSourceFileDescription}>
            <Textarea
              label={isYoutube ? 'Description' : 'File Description'}
              withAsterisk
              placeholder="Enter a description for this content"
              value={pendingDescription ?? ''}
              onChange={event => {
                setPendingDescription(event.currentTarget.value)
                if (descriptionError) setDescriptionError(null)
              }}
              error={descriptionError}
              minRows={3}
              autosize
              disabled={fieldsDisabled}
            />
          </AIInputWrapper>

          {/* Extracted Content (non-YouTube) */}
          {!isYoutube && (
            <Stack gap="xs">
              <Group justify="space-between">
                <InputLabel label="Extracted Content" withAsterisk />
                <Button variant="light" size="md" radius="md" onClick={openDataContentModal} disabled={!hasContent}>
                  Edit
                </Button>
              </Group>
              <Textarea
                value={contentPreview}
                disabled
                placeholder="Enter your content here"
                radius="md"
                variant="filled"
                size="sm"
                rows={6}
                style={{ border: '1px solid var(--mantine-color-gray-3)', borderRadius: theme.radius.md }}
              />
            </Stack>
          )}
        </Stack>
      </Paper>

      {/* FAQs Section */}
      {hasContent && (
        <Paper shadow="sm" p="md" radius="md">
          <Text size="lg" fw={600}>
            Add FAQs
          </Text>
          <FAQSection {...props} type="step" ref={faqSectionRef} disabled={aiDisabled} />
        </Paper>
      )}

      <Group gap="lg" justify="flex-end">
        <Button variant="light" color="gray" radius="md" disabled={isMutating} onClick={handleCancel}>
          Cancel
        </Button>

        <Button
          leftSection={<IconDeviceFloppy />}
          onClick={updateStep}
          disabled={!hasContent || isMutating}
          loading={isMutating}
          radius="md">
          Save and Exit
        </Button>
      </Group>
    </Stack>
  )
})
